import React, { useState } from "react";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import { fusionScrollBarStyles } from "@directv-digital/dds.core.commons";
import CardMedia from "@mui/material/CardMedia";
import { LocalRsnApiResponse } from "@directv-digital/dds.apis.local-rsn-chnls";
import ZipcodeModule from "@/video-lab/zipcode-module";
import { MyHomeRsnTableProps } from "../../package-carousel.props";

const styles = {
  sectionContainer: {
    display: "flex",
    width: "100%",
    flexDirection: "column"
  },
  rsnTableContainer: {
    display: "flex",
    justifyContent: {
      xs: "center",
      sm: "start"
    },
    flexDirection: "column",
    alignItems: "flex-start",
    alignContent: "flex-start",
    alignSelf: "stretch",
    flexWrap: "wrap",
    width: {
      xs: "100%",
      sm: "536px"
    },
    mb: "8px",
    opacity: 1,
    transform: "scaleY(1)", // : "scaleY(0.95)",
    transformOrigin: "top",
    transition:
      "max-height 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.3s, transform 0.4s",
    overflow: "hidden",
    willChange: "max-height, opacity, transform"
  },
  rsnTableHeaderContainer: {
    display: "flex",
    alignItems: "center",
    alignSelf: "stretch",
    justifyContent: "space-between",
    width: "100%"
  },
  rsnTableHeader: {
    textAlign: "center",
    flexShrink: 0,
    width: "calc(50% - 1px)",
    padding: {
      xs: "12px 30px",
      sm: "12px 20px",
      md: "12px 20px"
    },
    // border: "1px solid #D1D1D1",
    borderRadius: "8px 8px 0px 0px"
  },
  tableBodyContainer: {
    display: "flex",
    alignItems: "flex-start",
    alignSelf: "stretch",
    height: "100%",
    width: "100%",
    position: "relative"
  },
  rsnTableBody: {
    width: "50%",
    gap: "10px",
    display: "flex",
    padding: { xs: "8px 12px 8px 20px" },
    justifyContent: "start",
    flexDirection: "column",
    alignItems: "start",
    borderRight: "1px solid #D1D1D1",
    borderBottom: "1px solid #D1D1D1",
    borderLeft: "1px solid #D1D1D1"
  },
  channelLogoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    gap: "8px",
    position: "relative"
  },
  channelLogo: {
    display: "flex",
    width: "80px",
    height: "80px",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "300px"
  },
  zipCodeModuleContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    gap: "8px",
    position: "relative",
    width: "100%"
  },
  teamLogo: {
    height: "40px",
    width: "40px"
  },
  teamsWithLogoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px"
  }
};

const MyHomeRsnTable = (props: MyHomeRsnTableProps) => {
  const {
    title,
    legal,
    teams,
    teamsTitle,
    errorMessage,
    channelsTitle,
    channelName,
    channelNumber,
    teamName,
    tableBodyBackground
  } = props;
  const [myHomeTeamRsnChannels, setMyHomeTeamRsnChannels] = useState([]);

  const [_loading, setLoading] = useState(false);

  const handleLocalRsnChannelFilter = (rsnChannels: any) => {
    const filteredChannels = rsnChannels?.filter(localRsnChannel => {
      // RSN channels filter for MyHomeTeam genre
      const isMyHomeTeamEligible =
        localRsnChannel?.packageNames?.includes?.("MYHOMETEAM");
      const isMyHomeTeamHasTeams =
        localRsnChannel?.mlb ||
        localRsnChannel?.nhl ||
        localRsnChannel?.nba ||
        localRsnChannel?.wnba;
      return isMyHomeTeamEligible && isMyHomeTeamHasTeams;
    });
    setMyHomeTeamRsnChannels(filteredChannels);
  };

  const setLoadingLocalRsnChannels = React.useCallback(() => {
    setLoading(true);
  }, []);

  const addLocalRsnChnls = React.useCallback(
    (
      channels: LocalRsnApiResponse | null,
      _dma?: string,
      _nielsenDma?: string
    ) => {
      handleLocalRsnChannelFilter(channels?.rsnChannels ?? []);
    },
    []
  );

  const handleLocalRsnApiError = error => {
    setLoading(false);
    console.error("Channel Pack Lineup >> Local/Rsn API Error..", error);
  };

  const getTeamName = (channel: any, team: string) => {
    const teamData = channel?.[team];
    if (typeof teamData === "string") {
      return teamData;
    }
    if (teamData?.teamName) {
      return teamData.teamName;
    }
    return "";
  };

  try {
    return (
      <DtvBox sx={{ ...styles.sectionContainer }}>
        {/* Zip Code Module */}
        <DtvBox
          sx={{
            ...styles.zipCodeModuleContainer
          }}
        >
          <ZipcodeModule
            id="chnl-pck-lnp-zip-module"
            mode="light"
            position="left"
            size="small"
            onLocalRsnChnlsApiError={handleLocalRsnApiError}
            onLocalRsnChnlsConfirmed={addLocalRsnChnls}
            onSearchButtonClick={setLoadingLocalRsnChannels}
            localRsnApiOptions={{
 satellite: false,
stream: true
}}
            // ciConfig={props?.zipcodeModuleConfig}
          />
        </DtvBox>
        {/* In-Market Title */}
        {title && <DtvText {...title} sx={{ p: "0px 0px 8px 0px" }} />}
        {/* RSN teams table */}
        {myHomeTeamRsnChannels?.length > 0 ? (
          <DtvBox sx={{ ...styles.rsnTableContainer }}>
            {/* Table Header */}
            <DtvBox sx={{ ...styles.rsnTableHeaderContainer }}>
              <DtvText
                {...channelsTitle}
                sx={{
                  ...styles.rsnTableHeader,
                  background: channelsTitle?.background
                }}
              />
              <DtvText
                {...teamsTitle}
                sx={{
                  ...styles.rsnTableHeader,
                  background: teamsTitle?.background
                }}
              />
            </DtvBox>
            {/* Table Body */}
            {myHomeTeamRsnChannels?.map((channel, index) => (
              <DtvBox
                key={index}
                sx={{
                  ...styles.tableBodyContainer,
                  background: tableBodyBackground
                }}
              >
                {/* Channels */}
                <DtvBox sx={{ ...styles.rsnTableBody }}>
                  {/* cannel logo */}
                  <DtvBox
                    id={`icon-container-${channel?.network}`}
                    key={index}
                    sx={{ ...styles.channelLogoContainer }}
                    tabIndex={-1}
                    aria-label={`${channel?.network || "channel"} icon`}
                  >
                    <CardMedia
                      alt={channel?.network || "channel logo icon"}
                      aria-labelledby={`icon-container-${channel?.network}`}
                      component={"img"}
                      key={index}
                      loading="lazy"
                      src={`https://www.directv.com/${channel?.logo}`}
                      sx={{ ...styles.channelLogo }}
                    />
                  </DtvBox>
                  {/* channel name */}
                  <DtvText {...channelName} content={channel?.network} />
                  {/* channel number */}
                  <DtvText
                    {...channelNumber}
                    content={`${channelNumber?.content} ${channel?.channelNumber}`}
                  />
                </DtvBox>
                {/* Team(s) */}
                <DtvBox
                  sx={{
                    ...styles.rsnTableBody,
                    justifyContent: "flex-end",
                    alignItems: "start",
                    gap: "4px",
                    position: "absolute",
                    right: 0,
                    height: "100%",
                    width: "50%",
                    overflowY: "scroll",
                    ...fusionScrollBarStyles()
                  }}
                >
                  {teams?.map((team, index) => channel?.[team] && (
                    <DtvBox
                      key={index}
                      sx={{ ...styles.teamsWithLogoContainer }}
                    >
                      {channel?.[team]?.teamLogo && (
                        <CardMedia
                          alt={getTeamName(channel, team)}
                          aria-labelledby={`icon-container-${channel?.[team]?.teamName}`}
                          component={"img"}
                          loading="lazy"
                          src={`https://www.directv.com/${channel?.[team]?.teamLogo}`}
                          sx={{ ...styles.teamLogo }}
                          onError={e => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            target.src =
                              "https://www.directv.com/dtvassets/global/icons/fusion/alert-icon-error.svg";
                          }}
                        />
                      )}
                      <DtvText
                        {...teamName}
                        content={getTeamName(channel, team)}
                      />
                    </DtvBox>
                  ))}
                </DtvBox>
              </DtvBox>
            ))}
          </DtvBox>
        ) : (
          <DtvText {...errorMessage} sx={{
 color: "#102641",
mb: "12px"
}} />
        )}
        {/* Local RSN Legal */}
        {legal && <DtvText {...legal} />}
      </DtvBox>
    );
  } catch (error) {
    return <ErrorDisplay component="MyHomeRsnTable" error={error} />;
  }
};

export default React.memo(MyHomeRsnTable);
